import { useState, useEffect, useRef } from 'react'
import useSafeState from 'hooks/useSafeState'
import { useSubscription } from '@apollo/client'
import { SignalingClient } from 'amazon-kinesis-video-streams-webrtc'
import { useMutation } from 'components/graphql'
import { listenDeviceStatus, establishChannelConnection, closeChannelConnection } from '../gql'
import useCredentials from './useCredentials'
import throttle from 'lodash/throttle'
import versionCompare from 'helpers/versionCompare'

const useStreamViewer = ({ videoRef, hardwareId, clientId, appManagerVersion, onError, onMessage, onDeviceStateChange }) => {
	const [hasError, setHasError] = useSafeState(null)
	const [hasClosed, setHasClosed] = useSafeState(null)
	const [loadingPeerConnection, setLoadingPeerConnection] = useSafeState(true)
	const [isDataChannelReady, setIsDataChannelReady] = useSafeState(false)

	const viewer = useRef({})
	const deviceCaller = useRef(null)
	const stillAliveTimer = useRef(null)
	const initWaitTimeout = useRef(null)
	const sentMessages = useRef({})
	const sentMessageId = useRef(1)
	const peerConnectionTimeout = useRef(null)
	const isFirstFrameDetected = useRef(false)
	const isFirstFrameAckSent = useRef(false)
	const isFileStructureSent = useRef(false)
	const cleanupFirstFrameWatcher = useRef(null)

	const { data: credentials, loading: credentialsLoading } = useCredentials()
	const { data: statusData, loading: statusLoading } = useSubscription(listenDeviceStatus, {
		onError: setHasError,
		onSubscriptionData: ({ subscriptionData }) => {
			const streamStatus = subscriptionData?.data?.listenDeviceStatuses?.payload?.kinesisSessionStatus
			if(!streamStatus) {
				return null
			}
			logger('[DEVICE] status:', streamStatus)
			if(onDeviceStateChange) {
				onDeviceStateChange(streamStatus)
			}
			if(streamStatus === 'END_WITH_ERROR' || streamStatus === 'MEDIA_PROJECTION_FAIL' || streamStatus === 'WRONG_CONNECTION_PARAMS') {
				clearPeerConnectionTimeout()
				return setHasError({ type: 'DEVICE_ERROR', message: streamStatus })
			}
			if(streamStatus === 'END_SERVICE') {
				clearPeerConnectionTimeout()
				return setHasClosed(true)
			}
		},

		skip: typeof hardwareId === 'undefined',
		variables: {
			hardwareId: hardwareId
		}

	})
	const [mutate, { data: connectionData, loading: connectionDataLoading, called }] = useMutation(establishChannelConnection, {
		withIndicator: false,
		onError: setHasError,
		skip: typeof hardwareId === 'undefined',
		variables: {
			hardwareId: hardwareId
		}
	})
	const [endSession] = useMutation(closeChannelConnection, {
		withIndicator: false,
		onError: setHasError,
		onCompleted: () => {
			setHasClosed(true)
			stopCallDevice()
		},
		skip: typeof hardwareId === 'undefined',
		variables: {
			hardwareId: hardwareId
		}
	})

	const streamStatus = statusData?.listenDeviceStatuses?.payload?.kinesisSessionStatus
	const isDeviceReady = streamStatus === 'START_SUCCESS' || streamStatus === 'ALREADY_STARTED'
	const isDataLoading = connectionDataLoading || credentialsLoading || statusLoading

	const logger = (...args) => {
		/* eslint-disable-next-line */
		console.log(...args)
	}

	const initCallDevice = () => {
		logger('[INIT CALL] start')
		initWaitTimeout.current = setTimeout(() => {
			logger('[INIT CALL] timed out')
			setHasClosed(true)
			stopCallDevice()
		}, 60 * 1000)
		callDevice(0)
	}

	const callDevice = (delay) => {
		deviceCaller.current = setTimeout(() => {
			mutate()
			callDevice(delay ? 30 : 10) // 10 seconds for second call, 30 for subsequent
			logger('[INIT CALL] called')
		}, delay * 1000)
	}

	const stopCallDevice = () => {
		logger('[INIT CALL] stop')
		clearTimeout(deviceCaller.current)
		deviceCaller.current = null
		clearTimeout(initWaitTimeout.current)
		initWaitTimeout.current = null
	}

	const clearPeerConnectionTimeout = () => {
		clearTimeout(peerConnectionTimeout.current)
		peerConnectionTimeout.current = null
	}

	const endSessionOnPeerConnectionTimeout = () => {
		logger('[PEER CONNECTION] establish timeout reached (30s), closing session')
		clearPeerConnectionTimeout()
		setHasError({ type: 'PEER_CONNECTION_TIMEOUT', message: 'Peer connection was not established within 30 seconds' })
		setHasClosed(true)
		stopCallDevice()
		if(viewer.current.signalingClient) {
			viewer.current.signalingClient.close()
		}
		if(viewer.current.peerConnection) {
			viewer.current.peerConnection.close()
		}
		if(viewer.current.dataChannel) {
			viewer.current.dataChannel.close()
		}
	}

	const startPeerConnectionTimeout = () => {
		if(peerConnectionTimeout.current) {
			return
		}
		peerConnectionTimeout.current = setTimeout(() => {
			endSessionOnPeerConnectionTimeout()
		}, 30 * 1000)
	}

	const sendCommandMessage = (message) => {
		if(isDataChannelReady) {
			logger('[DEVICE] sending command: ', message)

			const encoder = new TextEncoder()

			// Use it like this:
			const messageString = JSON.stringify(message)
			const encodedMessage = encoder.encode(messageString)

			viewer.current.dataChannel.send(encodedMessage)
		}
	}

	// Send VIEWER_FRAME_READY only when BOTH conditions are met:
	// 1) first video frame has been rendered
	// 2) FILE_STRUCTURE command has been sent
	// Uses dataChannel.readyState directly to avoid stale React-state closures from event listeners.
	const maybeSendViewerFrameReadyAck = () => {
		if(!isFirstFrameDetected.current || !isFileStructureSent.current || isFirstFrameAckSent.current) {
			return
		}
		const dc = viewer.current.dataChannel
		if(!dc || dc.readyState !== 'open') {
			return
		}
		const payload = JSON.stringify({ command: 'VIEWER_FRAME_READY' })
		dc.send(new TextEncoder().encode(payload))
		isFirstFrameAckSent.current = true
		logger('[DATA CHANNEL] sent VIEWER_FRAME_READY')
	}

	// Called after every sendRequest — only acts when command is FILE_STRUCTURE
	const sendViewerFrameReadyAfterFileStructure = (command) => {
		if(command === 'FILE_STRUCTURE') {
			isFileStructureSent.current = true
			// Case 1: frame already rendered before FILE_STRUCTURE → send ACK now
			maybeSendViewerFrameReadyAck()
		}
	}

	const startFirstFrameWatcher = (videoElement) => {
		cleanupFirstFrameWatcher.current?.()
		cleanupFirstFrameWatcher.current = null
		isFirstFrameDetected.current = false
		isFirstFrameAckSent.current = false
		isFileStructureSent.current = false

		if(!videoElement) {
			return
		}

		const markFirstFrameReady = () => {
			if(isFirstFrameDetected.current) {
				return
			}
			isFirstFrameDetected.current = true
			logger('[VIDEO] first frame rendered')
			// Case 2: FILE_STRUCTURE was already sent → send ACK instantly
			maybeSendViewerFrameReadyAck()
		}

		if(typeof videoElement.requestVideoFrameCallback === 'function') {
			const callbackId = videoElement.requestVideoFrameCallback(() => {
				markFirstFrameReady()
			})
			cleanupFirstFrameWatcher.current = () => {
				if(typeof videoElement.cancelVideoFrameCallback === 'function') {
					videoElement.cancelVideoFrameCallback(callbackId)
				}
			}
			return
		}

		const onLoadedData = () => markFirstFrameReady()
		const onPlaying = () => markFirstFrameReady()
		videoElement.addEventListener('loadeddata', onLoadedData, { once: true })
		videoElement.addEventListener('playing', onPlaying, { once: true })

		cleanupFirstFrameWatcher.current = () => {
			videoElement.removeEventListener('loadeddata', onLoadedData)
			videoElement.removeEventListener('playing', onPlaying)
		}
	}

	/* requests should be sequential there is onnly one response listener
	   throttle is required as to ensure parallel requests will not occur */
	const sendRequestOld = throttle(async (commandMsg) => {
		if(viewer.current.dataChannel.responsePromise) {
			await viewer.current.dataChannel.responsePromise
		}

		viewer.current.dataChannel.responsePromise = new Promise((resolve, reject) => {
			if(!isDataChannelReady) {
				return reject('dataChannel is not opened')
			}
			sendCommandMessage(commandMsg)
			viewer.current.dataChannel.onmessage = event => {
				logger('[DATA CHANNEL] received: ', JSON.parse(event.data))
				return resolve(JSON.parse(event.data))
			}
		})
		return viewer.current.dataChannel.responsePromise
	}, 50)

	const sendRequest = (commandMsg) => new Promise((resolve, reject) => {
		if(!isDataChannelReady) {
			return reject('dataChannel is not opened')
		}
		if(!viewer.current.dataChannel.onmessage) {
			viewer.current.dataChannel.onmessage = event => {
				const received = JSON.parse(event.data)
				logger('[DATA CHANNEL] received: ', received)
				const resolverById = sentMessages.current[received.id]
				if(resolverById) {
					resolverById(received)
					sentMessages.current[received.id] = null
				}
				if(onMessage) {
					onMessage(received)
				}
			}
		}
		sendCommandMessage({
			id: sentMessageId.current,
			...commandMsg
		})
		sendViewerFrameReadyAfterFileStructure(commandMsg.command)
		sentMessages.current[sentMessageId.current] = resolve
		sentMessageId.current = sentMessageId.current + 1
	})

	const sendStillAlive = () => {
		stillAliveTimer.current = setTimeout(() => {
			sendCommandMessage({ command: 'STILL_ALIVE' })
			sendStillAlive()
		}, 10 * 1000)
	}


	// catch error for logging
	useEffect(() => {
		if(hasError) {
			clearPeerConnectionTimeout()
			logger('[ERROR]', hasError)
			if(onError) {
				onError(hasError)
			}
		}
	}, [hasError])

	// catch connection closed for logging
	useEffect(() => {
		if(hasClosed) {
			clearPeerConnectionTimeout()
			logger('[END] connection closed')
		}
	}, [hasClosed])

	// stop calling init when device is ready
	useEffect(() => {
		if(isDeviceReady && deviceCaller.current) {
			stopCallDevice()
		}
	})

	// start init call, and cleanup after dismount
	useEffect(() => {
		// IMPORTANT: call establishChannelConnection even if the device already reports ALREADY_STARTED.
		// Otherwise connectionData stays empty and signaling client cannot be configured, so no SDP offer is sent.
		if(!called && hardwareId) {
			initCallDevice()
		}
		return () => {
			logger('[END] cleaning up')
			clearPeerConnectionTimeout()
			cleanupFirstFrameWatcher.current?.()
			cleanupFirstFrameWatcher.current = null
			isFirstFrameDetected.current = false
			isFirstFrameAckSent.current = false
			isFileStructureSent.current = false
			if(viewer.current.signalingClient) {
				viewer.current.signalingClient.close()
			}
			if(viewer.current.peerConnection) {
				viewer.current.peerConnection.close()
			}
			if(viewer.current.dataChannel) {
				viewer.current.dataChannel.close()
			}
			clearTimeout(deviceCaller.current)
			clearTimeout(stillAliveTimer.current)
		}
	}, [])

	useEffect(() => {
		if(isDataChannelReady) {
			sendCommandMessage({ command: 'STILL_ALIVE' })
			sendStillAlive()
		}
	}, [isDataChannelReady])

	const canInitSignaling = !viewer.current.signalingClient && !isDataLoading && !hasError && !hasClosed && isDeviceReady
	if(canInitSignaling && !connectionData?.establishChannelConnection) {
		logger('[SIG CLIENT] skip setup: no establishChannelConnection payload yet')
	}

	if(canInitSignaling && connectionData?.establishChannelConnection) {
		logger('[SIG CLIENT] setting up')
		const { channelARN, endpointList, iceServerList } = connectionData.establishChannelConnection
		const { endpoint } = endpointList.find(i => i.protocol === 'WSS')

		viewer.current.signalingClient = new SignalingClient({
			channelARN: channelARN,
			channelEndpoint: endpoint,
			clientId,
			role: 'VIEWER',
			region: 'eu-west-1',
			credentials
		})

		const configuration = {
			iceServers: [
				...iceServerList.map(item => ({
					urls: item.uris,
					username: item.username,
					credential: item.password
				})),
				{ urls: `stun:stun.kinesisvideo.eu-west-1.amazonaws.com:443` }
			],
			iceTransportPolicy: 'all'
		}
		viewer.current.peerConnection = new RTCPeerConnection(configuration)

		const formValues = {
			useTrickleICE: true
		}


		viewer.current.dataChannel = viewer.current.peerConnection.createDataChannel(clientId)
		viewer.current.dataChannel.onopen = () => {
			logger('[DATA CHANNEL] opened')
			setIsDataChannelReady(true)
		}
		viewer.current.dataChannel.onclose = () => {
			logger('[DATA CHANNEL] closed, stoping still alive call')
			clearPeerConnectionTimeout()
			setIsDataChannelReady(false)
			setHasClosed(true)
			cleanupFirstFrameWatcher.current?.()
			cleanupFirstFrameWatcher.current = null
			isFirstFrameDetected.current = false
			isFirstFrameAckSent.current = false
			isFileStructureSent.current = false
			clearTimeout(stillAliveTimer.current)
			stillAliveTimer.current = null
			// Reject any pending sendRequest promises so callers don't hang
			Object.keys(sentMessages.current).forEach(id => {
				sentMessages.current[id] = null
			})
		}

		viewer.current.signalingClient.on('open', async () => {
			// Create an SDP offer to send to the master
			await viewer.current.peerConnection.setLocalDescription(
				await viewer.current.peerConnection.createOffer({
					offerToReceiveAudio: false,
					offerToReceiveVideo: true
				})
			)

			// When trickle ICE is enabled, send the offer now and then send ICE candidates as they are generated. Otherwise wait on the ICE candidates.
			if (formValues.useTrickleICE) {
				viewer.current.signalingClient.sendSdpOffer(viewer.current.peerConnection.localDescription)
			}
		})

		viewer.current.signalingClient.on('sdpAnswer', async answer => {
			// Add the SDP answer to the peer connection
			await viewer.current.peerConnection.setRemoteDescription(answer)
		})
		viewer.current.signalingClient.on('iceCandidate', candidate => {
			// Add the ICE candidate received from the MASTER to the peer connection
			viewer.current.peerConnection.addIceCandidate(candidate)
		})
		//
		// viewer.current.signalingClient.on('close', () => {
		// 	setHasClosed(true)
		// })
		viewer.current.signalingClient.on('error', error => {
			clearPeerConnectionTimeout()
			setHasError({ type: 'SIGNALING_CLIENT', message: error })
		})
		viewer.current.peerConnection.addEventListener('iceconnectionstatechange', event => {
			if (viewer.current.peerConnection.iceConnectionState === 'failed') {
				/* possibly reconfigure the connection in some way here */
				/* then request ICE restart */
				viewer.current.peerConnection.restartIce()
			}
		})
		viewer.current.peerConnection.addEventListener('icecandidate', ({ candidate }) => {
			// Send any ICE candidates to the other peer
			if (candidate) {
				// When trickle ICE is enabled, send the ICE candidates as they are generated.
				if (formValues.useTrickleICE) {
					viewer.current.signalingClient.sendIceCandidate(candidate)
				}
			} else if (!formValues.useTrickleICE) {
				viewer.current.signalingClient.sendSdpOffer(viewer.current.peerConnection.localDescription)
			}
		})
		viewer.current.peerConnection.addEventListener('connectionstatechange', event => {
			const status = event.target.connectionState
			logger('[PEER CONNECTION] status changed: ', status)
			if(status === 'connecting' && onDeviceStateChange) {
				onDeviceStateChange('connecting')
			}
			if(['new', 'checking', 'connecting'].includes(status)) {
				return setLoadingPeerConnection(true)
			}
			if(['disconnected', 'closed'].includes(status)) {
				clearPeerConnectionTimeout()
				return setHasClosed(true)
			}
			if(status === 'failed') {
				clearPeerConnectionTimeout()
				return setHasError({ type: 'PEER_CONNECTION' })
			}
			if(status === 'connected') {
				clearPeerConnectionTimeout()
				return setLoadingPeerConnection(false)
			}
		})
		viewer.current.peerConnection.addEventListener('track', event => {
			logger('[PEER CONNECTION] received video track')
			viewer.current.remoteStream = event.streams[0]
			videoRef.current.srcObject = viewer.current.remoteStream
			startFirstFrameWatcher(videoRef.current)
		})
	}
	if(viewer?.current?.signalingClient?.readyState === 3 && !hasClosed && !hasError) {
		logger('[SIG CLIENT] opening')
		startPeerConnectionTimeout()
		viewer.current.signalingClient.open()
	}

	return {
		isLoading: (hasClosed || hasError) ? false : (isDataLoading || loadingPeerConnection),
		hasClosed,
		hasError,
		sendCommandMessage,
		sendRequest: appManagerVersion && versionCompare(appManagerVersion, '1.5.3') < 0 ? sendRequestOld : sendRequest,
		isDataChannelReady,
		endSession
	}
}

export default useStreamViewer
